import type { ReactNode } from 'react';

interface DashboardCardProps {
  /** Kept for the layout config and callers; no longer drawn (plain headers, Jeff 2026-10-20). */
  icon?: string;
  title: string;
  count?: number;
  children: ReactNode;
  toolbar?: ReactNode;
  /** Optional right-anchored content inside the header row —
   *  small controls like a "Show All" checkbox that belong with
   *  the title rather than in a separate toolbar band. Pushed
   *  right via `margin-left: auto`. */
  headerRight?: ReactNode;
  severity?: 'info' | 'warning' | 'alert' | 'success';
  draggable?: boolean;
  onDragStart?: (e: React.DragEvent) => void;
  onDragOver?: (e: React.DragEvent) => void;
  onDrop?: (e: React.DragEvent) => void;
  onDragEnd?: () => void;
  /**
   * Move this card one place earlier (-1) or later (1) in the layout.
   *
   * Reordering was drag-and-drop only, so arranging the dashboard could not be
   * done from the keyboard at all. Supplied alongside the drag handlers in
   * edit mode.
   */
  onMove?: (direction: -1 | 1) => void;
  /** Position among the visible cards, for the announced label. */
  movePosition?: { index: number; total: number };
}

export default function DashboardCard({
  title,
  count,
  children,
  toolbar,
  headerRight,
  severity,
  draggable,
  onDragStart,
  onDragOver,
  onDrop,
  onDragEnd,
  onMove,
  movePosition,
}: DashboardCardProps) {
  // In edit mode the grid is a reorderable list, so each card is an option
  // within it (see the listbox role on the grid in Dashboard.tsx). Outside
  // edit mode the card is ordinary content and takes no role, no tab stop and
  // no key handling.
  const reorderLabel = movePosition
    ? `${title}, card ${movePosition.index + 1} of ${movePosition.total}. ` +
      'Press Ctrl plus the up or down arrow to move it.'
    : `${title}. Press Ctrl plus the up or down arrow to move it.`;

  const onReorderKeyDown = (e: React.KeyboardEvent) => {
    if (!onMove || !e.ctrlKey) return;
    if (e.key !== 'ArrowUp' && e.key !== 'ArrowDown') return;
    e.preventDefault();
    onMove(e.key === 'ArrowUp' ? -1 : 1);
  };

  return (
    <div
      className="dashboard-card"
      draggable={draggable}
      onDragStart={onDragStart}
      onDragOver={onDragOver}
      onDrop={onDrop}
      onDragEnd={onDragEnd}
      // Edit mode only: a focusable, labelled group that Ctrl+Arrow reorders.
      //
      // `group` rather than `option`: an option belongs to a listbox and
      // implies selection, and these cards are reordered, not selected.
      // Claiming otherwise would satisfy the linter by lying to screen
      // readers, which is the wrong trade.
      //
      // jsx-a11y still reports two findings here — a tabIndex on a
      // non-interactive role, and interactions on a static element. Both are
      // accurate descriptions of an intermediate state: the keyboard path now
      // works, but the fully correct structure is a reorderable list at the
      // grid level rather than per-card roles. Left flagged rather than
      // suppressed so that work stays visible in the count.
      role={draggable ? 'group' : undefined}
      tabIndex={draggable ? 0 : undefined}
      aria-label={draggable ? reorderLabel : undefined}
      onKeyDown={draggable ? onReorderKeyDown : undefined}
    >
      <div className="dashboard-card-wrapper" data-severity={severity}>
        <div className="dashboard-card-header">
          <span className="card-title">{title}</span>
          {count != null && count > 0 && (
            <span className="card-count">{count}</span>
          )}
          {headerRight && (
            <span className="card-header-right" style={{ marginLeft: 'auto' }}>
              {headerRight}
            </span>
          )}
        </div>
        {toolbar}
        <div className="dashboard-card-content">
          {children}
        </div>
      </div>
    </div>
  );
}

/**
 * What a card shows instead of rows: one line of plain text. `icon` is
 * accepted for existing callers but no longer drawn.
 */
function EmptyState({ message }: { message: string; icon?: string }) {
  return (
    <div className="dashboard-empty-state">
      <span>{message}</span>
    </div>
  );
}

function SkeletonCard({ title }: { icon?: string; title: string }) {
  return (
    <div className="dashboard-card">
      <div className="dashboard-card-wrapper">
        <div className="dashboard-card-header">
          <span className="card-title">{title}</span>
        </div>
        <div className="dashboard-card-content">
          <div className="dashboard-card-skeleton">
            <div className="skeleton-row skeleton-header-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row" />
            <div className="skeleton-row short" />
          </div>
        </div>
      </div>
    </div>
  );
}

export { EmptyState, SkeletonCard };
